import Immutable from 'immutable';
import moment from 'moment';
import { createSelector } from 'reselect';
import { id } from '../../../web/utilities/functions';
import { selectQuery, selectPayload } from '../../location/selectors';
import { selectAllInstitutions } from '../../Institutions/selectors';
import { selectPortalsByPath } from '../../Portals/selectors';
import {
  selectPageNumber,
  selectPageSize,
  selectCursor,
  selectTabName,
  selectSearch,
} from '../selectors';

export {
  selectPageNumber,
  selectPageSize,
  selectCursor,
  selectTabName,
  selectSearch,
};

const selectWasabiInstitutions = (state) =>
  state.Wasabi
    ? state.Wasabi.institutions
    : Immutable.fromJS({ institutions: {} });

const mergePortalAndInstitution = (institution, portal) => {
  let portalInstitution = institution;
  portalInstitution = portalInstitution.set('isOnline', true);
  if (portal) {
    portalInstitution = portalInstitution.set(
      'privacy',
      portal.get('isLive') ? 'public' : 'private'
    );
    portalInstitution = portalInstitution.set(
      'isOnline',
      portal.get('isOnline')
    );
  }
  return portalInstitution;
};

export const selectInstitutions = createSelector(
  selectWasabiInstitutions,
  selectAllInstitutions,
  selectPortalsByPath,
  (wasabi, institutions, portals) => {
    const instPaths = wasabi.getIn(
      ['institutions', 'institutionPaths'],
      Immutable.List()
    );

    return instPaths
      .map((path) => {
        if (!institutions.has(path)) {
          return null;
        }
        return mergePortalAndInstitution(
          institutions.get(path),
          portals.get(path)
        );
      })
      .filter(id)
      .sortBy((inst) => inst.get('name'));
  }
);

export const selectInstitutionsTotal = createSelector(
  selectWasabiInstitutions,
  (wasabi) => wasabi.getIn(['institutions', 'total'], 0)
);

export const selectInstitutionsRequestId = createSelector(
  selectWasabiInstitutions,
  (wasabi) => wasabi.getIn(['institutions', 'requestId'])
);

export const selectInstitutionsLastRequest = createSelector(
  selectWasabiInstitutions,
  (wasabi) => wasabi.getIn(['institutions', 'lastRequest'])
);

export const selectInstitutionsLoading = createSelector(
  selectWasabiInstitutions,
  (wasabi) => wasabi.getIn(['institutions', 'loading'])
);

export const selectInstitutionId = createSelector(selectPayload, (params) =>
  params.get('institutionId')
);

export const selectLastRequestedInstitution = createSelector(
  selectWasabiInstitutions,
  (wasabi) => wasabi.getIn(['institutions', 'lastRequestedInstitution'])
);

export const selectPortalInstitution = createSelector(
  selectInstitutionId,
  selectAllInstitutions,
  selectPortalsByPath,
  (institutionId, institutions, portals) => {
    const institution = institutions.get(institutionId);

    if (institution) {
      return mergePortalAndInstitution(institution, portals.get(institutionId));
    }
    return null;
  }
);

export const selectInstitutionCoursesRequestId = createSelector(
  selectWasabiInstitutions,
  (wasabi) => wasabi.getIn(['institutions', 'courses', 'requestId'], 0)
);

export const selectInstitutionCoursesLastRequest = createSelector(
  selectWasabiInstitutions,
  (wasabi) => wasabi.getIn(['institutions', 'courses', 'lastRequest'])
);

export const selectInstitutionCourses = createSelector(
  selectWasabiInstitutions,
  (wasabi) => wasabi.getIn(['institutions', 'courses'])
);

export const selectInstitutionUsage = createSelector(
  selectWasabiInstitutions,
  selectInstitutionId,
  (wasabi, institution) =>
    wasabi.getIn(['institutions', 'usage', institution], Immutable.Map())
);

export const selectContractStartDate = createSelector(
  selectQuery,
  selectInstitutionUsage,
  (query, usage) => {
    if (query.has('startDate')) {
      const startDate = moment(query.get('startDate'));
      if (startDate.isValid() && startDate.isBefore()) {
        return startDate.format('YYYY-MM');
      }
    }
    // else default to the date the institution was created
    if (usage.get('createdOn')) {
      const startDate = moment(usage.get('createdOn'));
      if (startDate.isValid() && startDate.isBefore()) {
        return startDate.format('YYYY-MM');
      }
    }
    return null;
  }
);

export const selectBillingPeriodOptions = createSelector(
  selectContractStartDate,
  (startDate) => {
    const options = [{ label: '-', value: '' }];

    if (startDate && moment(startDate).isValid()) {
      let start = moment(startDate);
      while (start.isBefore()) {
        const end = moment(start).add(1, 'years');
        options.push({
          label: `01 ${start.format("MMM 'YY")} - 01 ${end.format("MMM 'YY")}`,
          value: start.format('YYYY-MM-DD'),
        });
        start = start.add(1, 'years');
      }
    }

    return options;
  }
);

export const selectBillingPeriod = createSelector(
  selectQuery,
  selectContractStartDate,
  selectBillingPeriodOptions,
  (query, contractStartDate, billingPeriodOptions) => {
    if (query.has('billingPeriod')) {
      const startDate = moment(query.get('billingPeriod'));
      if (startDate.isValid() && startDate.isBefore()) {
        const billingPeriod = startDate.format('YYYY-MM-DD');
        if (
          billingPeriodOptions.find((option) => option.value === billingPeriod)
        ) {
          return billingPeriod;
        }
      }
    }
    // else default to the date the institution was created
    if (contractStartDate) {
      const billingPeriod = moment(contractStartDate).format('YYYY-MM-DD');
      return billingPeriod;
    }
    return null;
  }
);

export const selectContractStartDateOptions = createSelector(
  selectInstitutionUsage,
  (usage) => {
    const options = [{ label: '-', value: '' }];
    if (usage.get('createdOn')) {
      const date = moment(usage.get('createdOn'));
      if (date.isValid()) {
        while (date.isBefore()) {
          options.push({
            label: date.format('MMM YYYY'),
            value: date.format('YYYY-MM'),
          });
          date.add(1, 'months');
        }
      }
    }

    return options;
  }
);

export const selectInstitutionUsageLoading = createSelector(
  selectInstitutionUsage,
  (usage) => usage.get('loading', true)
);

export const selectActiveUserChartData = createSelector(
  selectInstitutionUsage,
  selectInstitutionUsageLoading,
  (usage, loading) => {
    if (loading) {
      return Immutable.List();
    }

    let data = usage.get('activeUsers', Immutable.List());

    // chart should always show a 12 month period
    const MIN_DATA_POINTS = 12;

    while (data.size < MIN_DATA_POINTS) {
      const month = moment(data.last().get('month'));
      month.add(1, 'months');
      data = data.push(
        Immutable.Map({
          month: month.format('YYYY-MM'),
        })
      );
    }
    return data;
  }
);

export const selectAnnualActiveUsers = createSelector(
  selectInstitutionUsage,
  selectInstitutionUsageLoading,
  (usage, loading) =>
    loading
      ? 0
      : usage.get('activeUsers', Immutable.List()).last().get('total') || 0
);

export const selectInstitutionMembersData = createSelector(
  selectWasabiInstitutions,
  selectInstitutionId,
  (wasabi, institution) =>
    wasabi.getIn(['institutions', 'members', institution], Immutable.Map())
);

export const selectInstitutionMembersLoading = createSelector(
  selectInstitutionMembersData,
  (data) => data.get('loading', true)
);

export const selectInstitutionMembers = createSelector(
  selectInstitutionMembersData,
  (data) => {
    return data.get('memberships', Immutable.List());
  }
);

export const selectInstitutionRole = createSelector(selectQuery, (query) =>
  query.get('role', '')
);

export const selectPreviousPage = createSelector(
  selectInstitutionMembersData,
  (data) => {
    const previousPage = data.get('previousPage', null);
    return previousPage && JSON.stringify(previousPage);
  }
);

export const selectNextPage = createSelector(
  selectInstitutionMembersData,
  (data) => {
    const nextPage = data.get('nextPage', null);
    return nextPage && JSON.stringify(nextPage);
  }
);

export const selectIsAccredibleEnabled = createSelector(
  selectInstitutionId,
  selectWasabiInstitutions,
  (institutionId, wasabi) =>
    wasabi.getIn(['institutions', 'isAccredibleEnabled', institutionId], null)
);

export const selectPortalIsLowCostEnabled = createSelector(
  selectInstitutionId,
  selectPortalsByPath,
  (institutionId, portals) => portals.getIn([institutionId, 'lowCost'], false)
);

export const selectPortalIsCourseKeepAliveEnabled = createSelector(
  selectInstitutionId,
  selectPortalsByPath,
  (institutionId, portals) =>
    portals.getIn([institutionId, 'courseKeepAlive'], false)
);

export const selectPortalSubscriptionId = createSelector(
  selectInstitutionId,
  selectPortalsByPath,
  (institutionId, portals) =>
    portals.getIn([institutionId, 'subscriptionId'], null)
);
